// components/i18n/AddressDisplay.tsx
import React from 'react';
import { useCountry } from '@/contexts/CountryContext';
import { useAddressValidation } from '@/hooks/useAddressValidation';

interface Address {
  line1: string;
  line2?: string;
  city: string;
  state?: string; // US states
  county?: string; // UK counties
  postcode?: string; // UK postcodes
  zipCode?: string; // US zip codes
  country: string;
}

interface AddressDisplayProps {
  address: Address;
  className?: string;
  format?: 'full' | 'compact' | 'single-line';
}

/**
 * AddressDisplay component that formats addresses according to country conventions
 * UK: Line1, Line2, City, County, Postcode, Country
 * US: Line1, Line2, City, State ZipCode, Country
 */
export const AddressDisplay: React.FC<AddressDisplayProps> = ({ 
  address, 
  className = '',
  format = 'full'
}) => {
  const { currentCountryCode } = useCountry();

  if (!address) {
    return <span className={className}>No address provided</span>;
  }

  const formatAddress = () => {
    const parts: string[] = [];

    // Add address lines
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);

    // Country-specific formatting
    if (currentCountryCode === 'US' || address.zipCode) {
      // US format: City, State ZipCode
      if (address.city) {
        let cityLine = address.city;
        if (address.state) {
          cityLine += `, ${address.state}`;
        }
        if (address.zipCode) {
          cityLine += ` ${address.zipCode}`;
        }
        parts.push(cityLine);
      }
    } else {
      // UK format: City, County, Postcode
      if (address.city) parts.push(address.city);
      if (address.county) parts.push(address.county);
      if (address.postcode) parts.push(address.postcode);
    }

    // Add country if different from current country
    if (address.country && address.country !== currentCountryCode) {
      parts.push(address.country);
    }

    return parts.filter(Boolean);
  };

  const addressParts = formatAddress();

  if (format === 'single-line') {
    return (
      <span className={className}>
        {addressParts.join(', ')}
      </span>
    );
  }

  if (format === 'compact') {
    return (
      <div className={className}>
        {addressParts.map((part, index) => (
          <span key={index}>
            {part}
            {index < addressParts.length - 1 && ', '}
          </span>
        ))}
      </div>
    );
  }

  // Full format - each part on a new line
  return (
    <div className={className}>
      {addressParts.map((part, index) => (
        <div key={index}>{part}</div>
      ))}
    </div>
  );
};

interface AddressFormProps {
  address: Partial<Address>;
  onChange: (address: Partial<Address>) => void;
  className?: string;
  disabled?: boolean;
  forceCountry?: 'US' | 'GB'; // Force a specific country format
}

/**
 * AddressForm component that provides country-appropriate address input fields
 */
export const AddressForm: React.FC<AddressFormProps> = ({
  address,
  onChange,
  className = '',
  disabled = false,
  forceCountry,
}) => {
  const { currentCountry } = useCountry();
  
  // Import the address validation hook
  const addressValidation = useAddressValidation(forceCountry);

  const handleChange = (field: keyof Address, value: string) => {
    onChange({
      ...address,
      [field]: value,
    });
  };

  const effectiveCountry = forceCountry || currentCountry?.code;
  const isUS = effectiveCountry === 'US';
  const isUK = effectiveCountry === 'GB';

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Address Line 1 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 1 *
        </label>
        <input
          type="text"
          value={address.line1 || ''}
          onChange={(e) => handleChange('line1', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Street address"
          disabled={disabled}
          required
          data-testid="address-line1"
        />
      </div>

      {/* Address Line 2 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 2
        </label>
        <input
          type="text"
          value={address.line2 || ''}
          onChange={(e) => handleChange('line2', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Apartment, suite, etc."
          disabled={disabled}
          data-testid="address-line2"
        />
      </div>

      {/* City */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          City *
        </label>
        <input
          type="text"
          value={address.city || ''}
          onChange={(e) => handleChange('city', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="City"
          disabled={disabled}
          required
          data-testid="address-city"
        />
      </div>

      {/* Country-specific fields */}
      {isUS && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <input
              type="text"
              value={address.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="State"
              disabled={disabled}
              required
              data-testid="address-state"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {addressValidation.getFieldLabel('zipCode')} *
            </label>
            <input
              type="text"
              value={address.zipCode || ''}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={addressValidation.getFieldPlaceholder('zipCode')}
              disabled={disabled}
              required
              data-testid="address-zipcode"
            />
          </div>
        </>
      )}

      {isUK && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              County
            </label>
            <input
              type="text"
              value={address.county || ''}
              onChange={(e) => handleChange('county', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="County"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {addressValidation.getFieldLabel('postcode')} *
            </label>
            <input
              type="text"
              value={address.postcode || ''}
              onChange={(e) => handleChange('postcode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={addressValidation.getFieldPlaceholder('postcode')}
              disabled={disabled}
              required
            />
          </div>
        </>
      )}

      {/* Country */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country *
        </label>
        <input
          type="text"
          value={address.country || currentCountry?.name || ''}
          onChange={(e) => handleChange('country', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Country"
          disabled={disabled}
          required
        />
      </div>
    </div>
  );
};

export default AddressDisplay; 